<script lang="ts" setup>
import { useRoute } from 'vue-router';
import type { z } from 'zod';

import { LoginEmailSchema } from '~/schemas/auth';

type State = Partial<z.infer<typeof LoginEmailSchema>>;

definePageMeta({
  middleware: 'auth-and-menu'
});

const route = useRoute();
const authStore = useAuthStore();

useSeoMeta({
  title: 'Forgot Password'
});

const state = reactive<State>({
  email: route.query.email as string
});

const displaySuccessMessage = ref<boolean>(false);

const handleSubmit = async (): Promise<void> => {
  displaySuccessMessage.value = false;

  if (!state.email) {
    return;
  }

  await authStore.forgotPassword(state.email.toLowerCase().trim());
  displaySuccessMessage.value = true;
};

const onSubmit = (): void => {
  handleSubmit();
};
</script>

<template>
  <UContainer class="forgotpassword min-h-svh flex flex-col items-center">
    <UForm
      class="py-4 sm:pt-20 w-full max-w-96"
      :schema="LoginEmailSchema"
      :state="state"
      @submit="onSubmit()"
    >
      <UCard
        class="shadow-2xl"
        :ui="{ body: { base: 'space-y-4' } }"
      >
        <template #header>
          <div class="text-center">
            <span class="font-semibold text-2xl">
              Forgot your password?
            </span>
          </div>
        </template>

        <UFormGroup
          label="Email"
          name="email"
        >
          <UInput
            v-model="state.email"
            icon="ic:round-mail"
            size="xl"
            data-test="email"
          />
        </UFormGroup>

        <template #footer>
          <UButton
            block
            class="bg-company-1 py-4 font-semibold uppercase"
            label="Request a new password"
            type="submit"
            data-test="submit"
          />

          <div
            v-if="displaySuccessMessage"
            class="mt-4 p-4 border-s-4 border-green-600 rounded bg-card-contrast font-semibold"
            data-test="success-message"
          >
            <span>
              An email was sent to you with instructions to reset your password. Please check your inbox.
            </span>
          </div>
        </template>
      </UCard>
    </UForm>
  </UContainer>
</template>

<style lang="scss" scoped>
.forgotpassword {
  padding-top: var(--header-height);
}
</style>
